import { useState, useEffect, useRef, forwardRef } from 'react';
import { Zap, Copy, Check, ExternalLink, ArrowLeft, X, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerClose,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { useZaps } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useBtcPrice } from '@/hooks/useBtcPrice';
import { genUserName } from '@/lib/genUserName';
import type { Event } from 'nostr-tools';
import QRCode from 'qrcode';
import type { WebLNProvider } from "@webbtc/webln-types";

interface ZapDialogProps {
  eventId?: string;
  recipientPubkey?: string;
  event?: Event;
  children?: React.ReactNode;
  className?: string;
}

const presetAmounts = [1, 3, 5, 10, 20];

interface ZapContentProps {
  invoice: string | null;
  amount: number | string;
  comment: string;
  isZapping: boolean;
  qrCodeUrl: string;
  copied: boolean;
  webln: WebLNProvider | null;
  paymentConfirmed: boolean;
  handleZap: () => void;
  handleCopy: () => void;
  openInWallet: () => void;
  setAmount: (amount: number | string) => void;
  setComment: (comment: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  zap: (amount: number, comment: string) => void;
  usdAmount: string;
  setUsdAmount: (amount: string) => void;
  satsEquivalent: number;
  isLoadingRate: boolean;
  artistName: string;
  artistPicture?: string;
  pendingInvoices: Array<{ pubkey: string; amount: number; invoice: string }>;
  currentInvoiceIndex: number;
}

// Moved ZapContent outside of ZapDialog to prevent re-renders causing focus loss
const ZapContent = forwardRef<HTMLDivElement, ZapContentProps>(({
  invoice,
  amount,
  comment,
  isZapping,
  qrCodeUrl,
  copied,
  webln,
  paymentConfirmed,
  handleZap,
  handleCopy,
  openInWallet,
  setAmount,
  setComment,
  inputRef,
  zap,
  usdAmount,
  setUsdAmount,
  satsEquivalent,
  isLoadingRate,
  artistName,
  artistPicture,
  pendingInvoices,
  currentInvoiceIndex,
}, ref) => (
  <div ref={ref}>
    {invoice ? (
      <div className="flex flex-col h-full min-h-0">
        {paymentConfirmed ? (
          // Payment confirmation animation
          <div className="flex flex-col items-center justify-center flex-1 py-12 space-y-6">
            <div className="relative">
              {/* Animated checkmark circle */}
              <div className="animate-in zoom-in duration-500">
                <CheckCircle2 className="h-24 w-24 text-green-500" strokeWidth={1.5} />
              </div>
              {/* Pulse ring animation */}
              <div className="absolute inset-0 animate-ping">
                <CheckCircle2 className="h-24 w-24 text-green-500/30" strokeWidth={1.5} />
              </div>
            </div>
            <div className="text-center space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
              <h3 className="text-2xl font-bold">Payment Received!</h3>
              <p className="text-muted-foreground">
                Your ${usdAmount} ({amount} sats) zap has been confirmed
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Payment amount display */}
            <div className="text-left px-6 pt-3 pb-3">
              <div className="flex items-baseline justify-between gap-4">
                <div>
                  <div className="text-2xl font-bold">${usdAmount}</div>
                  <div className="text-muted-foreground">{amount} sats</div>
                </div>
                {/* Show payment counter if multiple invoices */}
                {pendingInvoices.length > 1 && (
                  <div className="text-sm font-medium text-muted-foreground">
                    Payment {currentInvoiceIndex + 1} of {pendingInvoices.length}
                  </div>
                )}
              </div>
            </div>

            <Separator />

        <div className="flex flex-col justify-center min-h-0 flex-1 px-6 py-6">
          {/* QR Code */}
          <div className="flex flex-col items-center justify-start space-y-4">
            <Card className="p-3 max-w-[280px] w-full">
              <CardContent className="p-0 flex justify-center">
                {qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt="Lightning Invoice QR Code"
                    className="w-full h-auto aspect-square max-w-full object-contain"
                  />
                ) : (
                  <div className="w-full aspect-square bg-muted animate-pulse rounded" />
                )}
              </CardContent>
            </Card>

            {/* Payment checking indicator */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Checking for payment...</span>
            </div>
          </div>

          {/* Invoice input */}
          <div className="space-y-2 mt-6">
            <Label htmlFor="invoice">Lightning Invoice</Label>
            <div className="flex gap-2 min-w-0">
              <Input
                id="invoice"
                value={invoice}
                readOnly
                className="font-mono text-xs min-w-0 flex-1 overflow-hidden text-ellipsis"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Payment buttons */}
          <div className="space-y-3 mt-6">
            {webln && (
              <Button
                onClick={() => {
                  const finalAmount = typeof amount === 'string' ? parseInt(amount, 10) : amount;
                  zap(finalAmount, comment);
                }}
                disabled={isZapping}
                className="w-full"
                size="lg"
              >
                <Zap className="h-4 w-4 mr-2" />
                {isZapping ? "Processing..." : "Pay with WebLN"}
              </Button>
            )}

            <Button
              variant="outline"
              onClick={openInWallet}
              className="w-full"
              size="lg"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in Lightning Wallet
            </Button>

            <div className="text-xs text-muted-foreground text-center">
              Scan the QR code or copy the invoice to pay with any Lightning wallet.
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    ) : (
      <>
        <div className="px-4 pb-2 space-y-6">
          {/* USD Amount Display - Large centered number */}
          <div className="text-center">
            <div className="relative inline-block">
              <input
                ref={inputRef}
                type="number"
                value={usdAmount}
                onChange={(e) => setUsdAmount(e.target.value)}
                className="text-6xl font-bold text-center bg-transparent border-none outline-none w-full min-w-[120px] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{ width: `${Math.max(usdAmount.length || 1, 1) * 0.6 + 1}em` }}
              />
              <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full text-6xl font-bold pr-1">$</span>
            </div>
          </div>

          {/* Sats equivalent badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
              <Zap className="h-3.5 w-3.5" />
              {isLoadingRate ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <span>{satsEquivalent.toLocaleString()} sats</span>
              )}
            </div>
          </div>

          {/* Preset USD amounts */}
          <div className="grid grid-cols-5 gap-3">
            {presetAmounts.map((preset) => (
              <Button
                key={preset}
                variant="outline"
                size="lg"
                onClick={() => setUsdAmount(String(preset))}
                className={cn(
                  "font-semibold text-base h-12",
                  usdAmount === String(preset) && "bg-primary text-primary-foreground border-primary"
                )}
              >
                ${preset}
              </Button>
            ))}
          </div>

          {/* Comment Field */}
          <div className="space-y-2">
            <Textarea
              id="comment"
              placeholder="Add a message (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="resize-none min-h-[80px]"
              rows={3}
            />
          </div>
        </div>

        <div className="px-4 pb-6">
          <Button
            onClick={handleZap}
            className="w-full h-12 text-base font-semibold"
            disabled={isZapping || satsEquivalent <= 0 || !usdAmount}
          >
            {isZapping ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating invoice...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </>
    )}
  </div>
));
ZapContent.displayName = 'ZapContent';

export function ZapDialog({ eventId, recipientPubkey, event, children, className }: ZapDialogProps) {
  const [open, setOpen] = useState(false);
  const { user } = useCurrentUser();
  const actualPubkey = recipientPubkey || event?.pubkey || '';
  const actualEventId = eventId || event?.id || '';
  const { data: author } = useAuthor(actualPubkey);
  const { toast } = useToast();
  const { webln, activeNWC } = useWallet();

  // Use the provided event for event zaps, or null for profile zaps
  const target = event || null;

  const { zap, isZapping, invoice, setInvoice, paymentConfirmed, pendingInvoices, currentInvoiceIndex } = useZaps(target, webln, activeNWC, actualPubkey, () => setOpen(false));
  const [amount, setAmount] = useState<number | string>(0);
  const [usdAmount, setUsdAmount] = useState<string>('5');
  const [comment, setComment] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Get artist information from event tags
  const artistPubkeyTag = event?.tags.find(([name]) => name === 'p')?.[1];
  const artistNameTag = event?.tags.find(([name]) => name === 'artist')?.[1];
  const trackTitle = event?.tags.find(([name]) => name === 'title')?.[1];

  // Fetch artist profile if we have an artist pubkey
  const { data: artistAuthor } = useAuthor(artistPubkeyTag || '');

  // Determine artist name and picture
  let artistName: string;
  let artistPicture: string | undefined;

  if (artistPubkeyTag && artistAuthor) {
    // We have an artist pubkey and profile data
    artistName = artistAuthor.metadata?.display_name || artistAuthor.metadata?.name || genUserName(artistPubkeyTag);
    artistPicture = artistAuthor.metadata?.picture;
  } else if (artistNameTag) {
    // We have an artist name but no pubkey (no avatar)
    artistName = artistNameTag;
    artistPicture = undefined;
  } else {
    // Fall back to uploader/recipient
    artistName = author?.metadata?.display_name || author?.metadata?.name || genUserName(actualPubkey);
    artistPicture = author?.metadata?.picture;
  }

  // Use smart cached BTC price
  const { btcPrice, isLoadingRate, error: priceError } = useBtcPrice();

  // Show toast if price fetch fails
  useEffect(() => {
    if (priceError) {
      toast({
        title: 'Exchange rate unavailable',
        description: 'Could not fetch current Bitcoin price',
        variant: 'destructive',
      });
    }
  }, [priceError, toast]);

  // Calculate sats equivalent from USD
  const satsEquivalent = usdAmount && btcPrice
    ? Math.round((parseFloat(usdAmount) / btcPrice) * 100_000_000)
    : typeof amount === 'number'
      ? amount
      : parseInt(amount, 10) || 0;

  // Update amount when USD changes
  useEffect(() => {
    if (usdAmount && btcPrice) {
      const sats = Math.round((parseFloat(usdAmount) / btcPrice) * 100_000_000);
      setAmount(sats);
    }
  }, [usdAmount, btcPrice]);

  useEffect(() => {
    if (actualEventId) {
      // Set default comment to empty string - platform and track title will be added automatically
      setComment('');
    }
  }, [actualEventId]);

  // Generate QR code
  useEffect(() => {
    let isCancelled = false;

    const generateQR = async () => {
      if (!invoice) {
        setQrCodeUrl('');
        return;
      }

      try {
        const url = await QRCode.toDataURL(invoice.toUpperCase(), {
          width: 512,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });

        if (!isCancelled) {
          setQrCodeUrl(url);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to generate QR code:', err);
        }
      }
    };

    generateQR();

    return () => {
      isCancelled = true;
    };
  }, [invoice]);

  const handleCopy = async () => {
    if (invoice) {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      toast({
        title: 'Invoice copied',
        description: 'Lightning invoice copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openInWallet = () => {
    if (invoice) {
      const lightningUrl = `lightning:${invoice}`;
      window.open(lightningUrl, '_blank');
    }
  };

  useEffect(() => {
    if (open) {
      setAmount(0);
      setUsdAmount('5');
      setInvoice(null);
      setCopied(false);
      setQrCodeUrl('');
      setComment('');
    } else {
      // Clean up state when dialog closes
      setAmount(0);
      setUsdAmount('5');
      setInvoice(null);
      setCopied(false);
      setQrCodeUrl('');
      setComment('');
    }
  }, [open, setInvoice]);

  const handleZap = () => {
    const finalAmount = satsEquivalent;
    if (finalAmount > 0) {
      // Format comment: {Platform}, {Track Title}: {user comment}
      // If no user comment, just show: {Platform}, {Track Title}
      let formattedComment = comment.trim();

      if (trackTitle) {
        // Format with platform and track title
        formattedComment = comment.trim()
          ? `NodeCast, ${trackTitle}: ${comment.trim()}`
          : `NodeCast, ${trackTitle}`;
      }

      zap(finalAmount, formattedComment);
    }
  };

  const contentProps = {
    invoice,
    amount: satsEquivalent,
    comment,
    isZapping,
    qrCodeUrl,
    copied,
    webln,
    paymentConfirmed,
    handleZap,
    handleCopy,
    openInWallet,
    setAmount,
    setComment,
    inputRef,
    zap,
    usdAmount,
    setUsdAmount,
    satsEquivalent,
    isLoadingRate,
    artistName,
    artistPicture,
    pendingInvoices,
    currentInvoiceIndex,
  };

  // Always show the dialog trigger for testing purposes
  // TODO: Re-enable validation in production
  // if (!user || user.pubkey === recipientPubkey || !author?.metadata?.lud06 && !author?.metadata?.lud16) {
  //   return null;
  // }

  if (isMobile) {
    // Use drawer for entire mobile flow, make it full-screen when showing invoice
    return (
      <Drawer
        open={open}
        onOpenChange={(newOpen) => {
          // Reset invoice when closing
          if (!newOpen) {
            setInvoice(null);
            setQrCodeUrl('');
          }
          setOpen(newOpen);
        }}
        dismissible={true} // Always allow dismissal via drag
        snapPoints={invoice ? [0.5, 0.75, 0.98] : [0.98]}
        activeSnapPoint={invoice ? 0.98 : 0.98}
        modal={true}
        shouldScaleBackground={false}
        fadeFromIndex={0}
      >
        <DrawerTrigger asChild>
          {children}
        </DrawerTrigger>
        <DrawerContent
          key={invoice ? 'payment' : 'form'}
          className={cn(
            "transition-all duration-300",
            invoice ? "h-full max-h-screen" : "max-h-[98vh]"
          )}
          data-testid="zap-modal"
        >
          <DrawerHeader className="text-center relative pb-4 px-4">
            {/* Back button when showing invoice (but not when confirmed) */}
            {invoice && !paymentConfirmed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInvoice(null);
                  setQrCodeUrl('');
                }}
                className="absolute left-4 top-4 flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}

            {/* Close button (hidden during confirmation animation) */}
            {!paymentConfirmed && (
              <DrawerClose asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-4 top-4"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DrawerClose>
            )}

            {!invoice && !paymentConfirmed && (
              <div className="flex items-center gap-3 pt-2">
                {artistPicture !== undefined && (
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarImage src={artistPicture} alt={artistName} />
                    <AvatarFallback>{artistName.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                )}
                <DrawerTitle className="text-lg font-semibold text-left">
                  Support {artistName}
                </DrawerTitle>
              </div>
            )}
            {invoice && !paymentConfirmed && (
              <DrawerTitle className="text-lg font-semibold pt-2">
                Lightning Payment
              </DrawerTitle>
            )}
            {paymentConfirmed && (
              <DrawerTitle className="text-lg font-semibold pt-2">
                Success!
              </DrawerTitle>
            )}
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <ZapContent {...contentProps} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[95vh] overflow-hidden p-0" data-testid="zap-modal">
        <DialogHeader className="pb-4 px-4 pt-6">
          {!invoice && !paymentConfirmed && (
            <div className="flex items-center gap-3">
              {artistPicture !== undefined && (
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={artistPicture} alt={artistName} />
                  <AvatarFallback>{artistName.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
              )}
              <DialogTitle className="text-lg font-semibold text-left">
                Support {artistName}
              </DialogTitle>
            </div>
          )}
          {invoice && !paymentConfirmed && (
            <DialogTitle className="text-lg font-semibold">
              Lightning Payment
            </DialogTitle>
          )}
          {paymentConfirmed && (
            <DialogTitle className="text-lg font-semibold">
              Success!
            </DialogTitle>
          )}
        </DialogHeader>
        <div className="overflow-y-auto">
          <ZapContent {...contentProps} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
